// Safety score helpers. We DO NOT compute the score — only translate
// backend-supplied numbers into UI affordances.
//
// CLAUDE.md §9: backend is the single source of truth (formula lives in
// `backend/internal/domain/safety_score/formula.go`). Backend also returns
// `level` directly — we keep `scoreToLevel` as a fallback only.

export type SafetyLevel = 'red' | 'yellow' | 'green';

export interface SafetyThresholds {
  red: number; // ≤ red → red
  yellow: number; // ≤ yellow (and > red) → yellow ; else green
}

export const DEFAULT_THRESHOLDS: SafetyThresholds = { red: 30, yellow: 50 };

export function scoreToLevel(
  score: number,
  thresholds: SafetyThresholds = DEFAULT_THRESHOLDS,
): SafetyLevel {
  if (score <= thresholds.red) return 'red';
  if (score <= thresholds.yellow) return 'yellow';
  return 'green';
}

// In Figma the headline shows "danger percentage" = 100 - safety_score.
// Returns rounded integer for display, clamped 0-100.
export function safetyToDangerPercent(score: number): number {
  const danger = 100 - score;
  if (!Number.isFinite(danger)) return 0;
  return Math.max(0, Math.min(100, Math.round(danger)));
}

// In Figma the headline on safe (green) regions shows the SAFETY percent
// (97%), but on yellow/red shows DANGER percent (64%, 3%). We mirror
// this by using safety for green and danger for non-green.
export function headlinePercent(score: number, level: SafetyLevel): number {
  if (level === 'green') return Math.round(score);
  return safetyToDangerPercent(score);
}
